import * as React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import type { VenueSourcesDto } from '@lib/dto/venue-sources.dto'
import { MAPPABLE_CHANNELS, SOURCE_CHANNEL_LABELS, mappableChannelSchema } from '@lib/enums/source-channel.enum'
import { dollarsFromCents, formatDate, possessive } from '@lib/helpers/format'
import { putVenueSourcesInputSchema, type PutVenueSourcesInput } from '@lib/input/venue-sources.input'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { useToast } from '@/components/ui/toast'
import { FormError } from '@/components/ui/form-error'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useSaveVenueSources, useVenueSources } from '@/hooks/useSources'
import { todayIso } from '@/lib/bookedDays'
import { centsToInput, inputToCents } from '@/lib/price'

interface CostDraft {
  key: string
  amount: string
  from: CalendarDay | ''
}

interface ListingDraft {
  key: string
  id: string | null
  name: string
  costs: CostDraft[]
}

interface NameDraft {
  key: string
  name: string
  leads: number
  /** '' when not mapped, a channel, or `listing:` and the listing draft's key. */
  choice: string
}

const LISTING = 'listing:'

let counter = 0
/** A key for a row made here, before it has an id. */
function newKey(): string {
  counter += 1
  return `new-${String(counter)}`
}

/** The channels a name can be mapped to directly; a listing site is chosen by name instead. */
const CHANNEL_CHOICES = MAPPABLE_CHANNELS.filter((c) => c !== 'listing')

function draftsOf(sources: VenueSourcesDto): { listings: ListingDraft[]; names: NameDraft[] } {
  return {
    listings: sources.listings.map((l) => ({
      key: l.id,
      id: l.id,
      name: l.name,
      costs: (l.costs ?? []).map((c) => ({ key: newKey(), amount: centsToInput(c.monthlyCents), from: c.from })),
    })),
    names: sources.names.map((n) => {
      let choice: string = n.channel ?? ''
      if (n.channel === 'listing') choice = n.listingId === null ? '' : `${LISTING}${n.listingId}`
      return { key: n.key, name: n.name, leads: n.leads, choice }
    }),
  }
}

/** What a listing costs now, or from when it will, in a line. */
function costSummary(costs: readonly CostDraft[], today: CalendarDay): string {
  const known = costs
    .flatMap((c) => {
      const cents = inputToCents(c.amount)
      return cents === null || c.from === '' ? [] : [{ cents, from: c.from }]
    })
    .sort((a, b) => a.from.localeCompare(b.from))
  const current = known.filter((c) => c.from <= today).at(-1)
  if (current !== undefined) return `${dollarsFromCents(current.cents)} a month since ${formatDate(current.from)}`
  const next = known[0]
  return next === undefined ? 'No cost yet' : `${dollarsFromCents(next.cents)} a month from ${formatDate(next.from)}`
}

/** The drafts as the API takes them, or what to fix first. */
function toInput(listings: readonly ListingDraft[], names: readonly NameDraft[], seesCosts: boolean): PutVenueSourcesInput | string {
  const input: PutVenueSourcesInput = { listings: [], mappings: [] }
  for (const listing of listings) {
    const label = listing.name.trim() === '' ? 'a listing site' : listing.name.trim()
    const costs: PutVenueSourcesInput['listings'][number]['costs'] = []
    for (const cost of seesCosts ? listing.costs : []) {
      const cents = inputToCents(cost.amount)
      if (cents === null) return `Enter what ${label} costs a month, in dollars.`
      if (cost.from === '') return `Choose the day each of ${possessive(label)} costs starts.`
      costs.push({ monthlyCents: cents, from: cost.from })
    }
    input.listings.push({ id: listing.id, name: listing.name, costs })
  }
  for (const name of names) {
    if (name.choice.startsWith(LISTING)) {
      const listing = listings.find((l) => `${LISTING}${l.key}` === name.choice)
      input.mappings.push({ name: name.name, channel: 'listing', listingName: listing?.name.trim() ?? null })
      continue
    }
    const channel = mappableChannelSchema.safeParse(name.choice)
    if (channel.success) input.mappings.push({ name: name.name, channel: channel.data, listingName: null })
  }
  const parsed = putVenueSourcesInputSchema.safeParse(input)
  return parsed.success ? parsed.data : (parsed.error.issues[0]?.message ?? 'Check the listing sites and lead sources.')
}

function SectionTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
      <p className="m-0 text-sm text-ink-muted">{hint}</p>
    </div>
  )
}

/** A listing's monthly costs, each from a date, changed here and saved with the rest. */
function CostsDialog({
  listing,
  onChange,
  onClose,
}: {
  listing: ListingDraft | null
  onChange: (costs: CostDraft[]) => void
  onClose: () => void
}) {
  const name = listing === null || listing.name.trim() === '' ? 'This listing site' : listing.name.trim()
  const costs = listing?.costs ?? []
  const update = (key: string, patch: Partial<CostDraft>): void => onChange(costs.map((c) => (c.key === key ? { ...c, ...patch } : c)))
  return (
    <Dialog open={listing !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>What {name} costs</DialogTitle>
          <DialogDescription>
            A monthly cost from each date on. A change starts on its date, so the periods before it keep the earlier cost.
          </DialogDescription>
        </DialogHeader>
        {costs.length === 0 ? <p className="m-0 text-sm text-ink-muted">No cost yet.</p> : null}
        <ul className="m-0 list-none space-y-2 p-0">
          {costs.map((cost) => (
            <li key={cost.key} className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-ink-muted">$</span>
              <Input
                size="sm"
                inputMode="decimal"
                aria-label="Monthly cost"
                className="w-28 text-right tabular-nums"
                value={cost.amount}
                onChange={(e) => update(cost.key, { amount: e.target.value })}
              />
              <span className="text-sm text-ink-muted">a month from</span>
              <DatePicker value={cost.from} onChange={(from) => update(cost.key, { from })} placeholder="Date" />
              <Button variant="ghost" size="sm" aria-label="Remove this cost" onClick={() => onChange(costs.filter((c) => c.key !== cost.key))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
        <div>
          <Button variant="outline" size="sm" onClick={() => onChange([...costs, { key: newKey(), amount: '', from: '' }])}>
            <Plus className="h-3.5 w-3.5" />
            Add a cost from a date
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Where a venue's couples come from (docs/ad-attribution.spec.md): the listing
 * sites it pays for and what each costs, and what each lead source name its
 * couples have in GoHighLevel or HoneyBook means. Saved whole.
 */
export function VenueSourcesSection({ venueId }: { venueId: string }) {
  const query = useVenueSources(venueId)
  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  // Re-made from each saved copy, so new listings pick up the ids they were given.
  return <VenueSourcesEditor key={query.dataUpdatedAt} venueId={venueId} sources={query.data} />
}

function VenueSourcesEditor({ venueId, sources }: { venueId: string; sources: VenueSourcesDto }) {
  const { toast } = useToast()
  const save = useSaveVenueSources(venueId)
  const initial = React.useMemo(() => draftsOf(sources), [sources])
  const [listings, setListings] = React.useState<ListingDraft[]>(initial.listings)
  const [names, setNames] = React.useState<NameDraft[]>(initial.names)
  const [editingCosts, setEditingCosts] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const today = todayIso()
  const seesCosts = sources.seesCosts

  const updateListing = (key: string, patch: Partial<ListingDraft>): void =>
    setListings((all) => all.map((l) => (l.key === key ? { ...l, ...patch } : l)))
  const removeListing = (key: string): void => {
    setListings((all) => all.filter((l) => l.key !== key))
    // Its names go back to not mapped, as they will once it's gone.
    setNames((all) => all.map((n) => (n.choice === `${LISTING}${key}` ? { ...n, choice: '' } : n)))
  }

  function handleSave(): void {
    const input = toInput(listings, names, seesCosts)
    if (typeof input === 'string') {
      setError(input)
      return
    }
    setError(null)
    save.mutate(input, {
      onSuccess: () => toast('Saved.'),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <SectionTitle
          title="Listing sites"
          hint="The sites this venue pays to be listed on. Map their lead sources below so their couples count toward them."
        />
        {listings.length === 0 ? <p className="m-0 text-sm text-ink-muted">No listing sites yet.</p> : null}
        <ul className="m-0 list-none p-0">
          {listings.map((listing) => {
            const label = listing.name.trim() === '' ? 'this listing site' : listing.name.trim()
            return (
              <li key={listing.key} className="flex flex-wrap items-center gap-3 border-b border-line py-2.5 last:border-b-0">
                <Input
                  size="sm"
                  aria-label="Listing site"
                  className="min-w-[12rem] flex-1"
                  value={listing.name}
                  onChange={(e) => updateListing(listing.key, { name: e.target.value })}
                  placeholder="The Knot"
                />
                {seesCosts ? (
                  <>
                    <span className="text-sm text-ink-muted">{costSummary(listing.costs, today)}</span>
                    <Button variant="outline" size="sm" onClick={() => setEditingCosts(listing.key)}>
                      Costs
                    </Button>
                  </>
                ) : null}
                <Button variant="ghost" size="sm" aria-label={`Remove ${label}`} onClick={() => removeListing(listing.key)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            )
          })}
        </ul>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const key = newKey()
            setListings((all) => [...all, { key, id: null, name: '', costs: [] }])
          }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add a listing site
        </Button>
      </section>

      <section className="space-y-3">
        <SectionTitle
          title="Lead sources"
          hint="Each lead source this venue’s couples have in GoHighLevel or HoneyBook, and what it means. One not mapped counts by how GoHighLevel saw the couple’s first visit, when it saw one. A couple who came from a Google or Meta ad counts there, whatever their lead source says."
        />
        {names.length === 0 ? (
          <p className="m-0 text-sm text-ink-muted">None of this venue’s couples has a lead source yet.</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {names.map((name) => (
              <li key={name.key} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-2.5 last:border-b-0">
                <span className="min-w-0 text-sm text-ink">
                  {name.name}{' '}
                  <span className="text-ink-muted">
                    · {name.leads} {name.leads === 1 ? 'lead' : 'leads'}
                  </span>
                </span>
                <Select
                  size="sm"
                  aria-label={`What “${name.name}” means`}
                  value={name.choice}
                  onChange={(e) => {
                    const choice = e.target.value
                    setNames((all) => all.map((n) => (n.key === name.key ? { ...n, choice } : n)))
                  }}
                >
                  <option value="">Not mapped</option>
                  {CHANNEL_CHOICES.map((channel) => (
                    <option key={channel} value={channel}>
                      {SOURCE_CHANNEL_LABELS[channel]}
                    </option>
                  ))}
                  {listings.length > 0 ? (
                    <optgroup label="Listing sites">
                      {listings.map((listing) => (
                        <option key={listing.key} value={`${LISTING}${listing.key}`}>
                          {listing.name.trim() === '' ? 'Unnamed listing site' : listing.name.trim()}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                </Select>
              </li>
            ))}
          </ul>
        )}
      </section>

      {error === null ? null : <FormError>{error}</FormError>}
      <FormActions>
        <Button
          variant="outline"
          onClick={() => {
            setListings(initial.listings)
            setNames(initial.names)
            setError(null)
          }}
          disabled={save.isPending}
        >
          Discard changes
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </FormActions>

      <CostsDialog
        listing={listings.find((l) => l.key === editingCosts) ?? null}
        onChange={(costs) => {
          if (editingCosts !== null) updateListing(editingCosts, { costs })
        }}
        onClose={() => setEditingCosts(null)}
      />
    </div>
  )
}
